// Chakra imports
import {
  Flex,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  useColorModeValue,
  Box,
} from '@chakra-ui/react';

// Quita comas y cualquier caracter que no sea dígito o punto decimal.
const limpiarNumero = (valor: string): string => {
  const sinComas = valor.replace(/,/g, '');
  const soloValido = sinComas.replace(/[^0-9.]/g, '');
  const partes = soloValido.split('.');
  return partes.length > 2 ? `${partes[0]}.${partes.slice(1).join('')}` : soloValido;
};

// Agrega comas de miles al valor numérico "en limpio" para mostrarlo mientras se escribe.
const formatearConComas = (valor: string): string => {
  if (!valor) return '';
  const [entero, decimal] = valor.split('.');
  const enteroConComas = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimal !== undefined ? `${enteroConComas}.${decimal}` : enteroConComas;
};

export default function CurrencyField(props: {
  id?: string;
  label?: string;
  extra?: JSX.Element;
  placeholder?: string;
  required?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  mb?: string;
  [x: string]: any;
}) {
  const {
    id, label, extra, placeholder, required, isInvalid, isDisabled, value, onChange, mb, ...rest
  } = props;

  const textColorPrimary = useColorModeValue('gray.800', 'whiteAlpha.900');
  const textColorSecondary = useColorModeValue('gray.500', 'gray.400');
  const bgPrimary = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBorderColor = useColorModeValue('gray.300', 'gray.500');
  const focusBorderColor = useColorModeValue('blue.400', 'blue.300');
  const invalidBorderColor = useColorModeValue('red.400', 'red.300');
  const readOnlyBg = useColorModeValue('gray.100', 'gray.800');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorLimpio = limpiarNumero(e.target.value);
    onChange?.({ target: { id, value: valorLimpio } } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <Flex direction="column" mb={mb ?? '24px'} width="100%">
      {label && (
        <FormLabel htmlFor={id} fontSize="sm" color={textColorPrimary} fontWeight="semibold" mb="6px">
          {label}
          {required && <Text as="span" color="red.400" ml="1">*</Text>}
          {extra && (
            <Text fontSize="xs" ml="2" color={textColorSecondary} fontWeight="normal">
              {extra}
            </Text>
          )}
        </FormLabel>
      )}

      <Box position="relative">
        <InputGroup>
          <InputLeftElement pointerEvents="none" color={textColorSecondary} fontSize="sm" fontWeight="medium">
            $
          </InputLeftElement>
          <Input
            {...rest}
            id={id}
            inputMode="decimal"
            placeholder={placeholder}
            value={formatearConComas(value || '')}
            onChange={handleChange}
            isDisabled={isDisabled}
            fontWeight="medium"
            variant="outline"
            fontSize="sm"
            borderRadius="10px"
            borderWidth="1px"
            borderColor={isInvalid ? invalidBorderColor : borderColor}
            color={isDisabled ? textColorSecondary : textColorPrimary}
            bg={isDisabled ? readOnlyBg : bgPrimary}
            _placeholder={{ fontWeight: 'normal', color: textColorSecondary }}
            _hover={{ borderColor: isInvalid ? invalidBorderColor : hoverBorderColor }}
            _focus={{
              borderColor: isInvalid ? invalidBorderColor : focusBorderColor,
              boxShadow: `0 0 0 2px ${isInvalid ? invalidBorderColor : focusBorderColor}`,
            }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          />
          <InputRightElement pointerEvents="none" width="3.5rem">
            <Text fontSize="xs" color={textColorSecondary}>MXN</Text>
          </InputRightElement>
        </InputGroup>
      </Box>
    </Flex>
  );
}
